import { useEffect, useRef, useState } from "react";
import { Button, Card, Dropdown, Flex, Input, Typography, message } from "antd";
import type { MenuProps } from "antd";
import { ContactsOutlined, PlusOutlined, WhatsAppOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { LateralModal } from "@/components/modals/LateralModal";
import { ContactsCreate } from "@/pages/contacts/contactsCreate";
import { useGo } from "@refinedev/core";
import { useSelectedStores } from "@/config/context/SelectedItemsContext";
import { axiosInstance } from "@/utilities";
import { API_URL_CONTACTS, API_URL_QUOTES } from "@/services/XCONS_ENV";
import { ChannelPickerDropdown, useWritableChannels } from "@/components/channel-picker";
import { isWhatsappFamily } from "@/services/channel-types";
import { PresupuestoIcon, RelationIcon } from "@/components/icon";

const { Text } = Typography;

const ICON_SIZE = 14;

type Store = { store_id?: number; storeId?: number; code?: string; name: string };

type ContactRow = {
  contactId: number;
  /**
   * El id de la IDENTIDAD, que no es el de la ficha.
   *
   * Hace falta para crear un presupuesto a nombre de este contacto: `puntoseco_wishlist.customer_id`
   * y `assignCustomerAsOwner` trabajan con `user_id`, no con `contact_id`.
   */
  userId: number | null;
  name: string;
  email: string;
  whatsapp: string;
  storeCode: string;
  createdAt: string;
};

// ── Per-row component so each can have its own useWritableChannels hook ───────

const ClientRow = ({
  contact,
  isLast,
  stores,
  selectedStores,
  onCreateQuote,
}: {
  contact: ContactRow;
  isLast: boolean;
  stores: Store[];
  selectedStores: { code: string }[];
  /** Recibe el `user_id` del contacto, no el `contact_id`: es lo que espera el backend. */
  onCreateQuote: (userId: number | null, storeCode: string) => void;
}) => {
  const go = useGo();
  const { channels, unavailable, loading, failed, load } = useWritableChannels(
    contact.storeCode,
    contact.whatsapp,
    { iniciaConPlantilla: true },
  );

  const whatsappChannels = channels.filter((c) => isWhatsappFamily(c.type));

  const handlePick = (channelId: string) => {
    const params = new URLSearchParams({
      newMsg: "msg",
      channelId,
      contactId: String(contact.contactId),
      contactName: contact.name,
      whatsapp: contact.whatsapp,
      email: contact.email,
      storeCode: contact.storeCode,
    });
    window.open(`/conversations?${params.toString()}`, "_blank");
  };

  const quoteStoreMenu: MenuProps = {
    items: stores.map((s) => ({
      key: String(s.code ?? s.store_id ?? s.storeId),
      label: s.name,
    })),
    onClick: ({ key }) => onCreateQuote(contact.userId, key),
  };

  const presupuestoIcon = (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      <PresupuestoIcon style={{ width: ICON_SIZE, height: ICON_SIZE, color: "#15744b" }} />
    </span>
  );

  const quoteButton = selectedStores.length === 1 ? (
    <span
      style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
      onClick={() => onCreateQuote(contact.userId, selectedStores[0].code)}
    >
      {presupuestoIcon}
    </span>
  ) : (
    <Dropdown menu={quoteStoreMenu} trigger={["click"]} placement="bottomRight">
      <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
        {presupuestoIcon}
      </span>
    </Dropdown>
  );

  const hasWhatsapp = !!contact.whatsapp;

  const whatsappIcon = hasWhatsapp ? (
    <ChannelPickerDropdown
      channels={whatsappChannels}
      loading={loading}
      failed={failed}
      unavailable={unavailable}
      emptyText="No hay canales de WhatsApp disponibles"
      onPick={handlePick}
      onOpen={load}
      placement="bottomRight"
    >
      <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
        <WhatsAppOutlined style={{ fontSize: ICON_SIZE, color: "#25D366" }} />
      </span>
    </ChannelPickerDropdown>
  ) : (
    <span
      style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
      onClick={() => message.info("El cliente no tiene WhatsApp")}
    >
      <WhatsAppOutlined style={{ fontSize: ICON_SIZE, color: "rgba(0,0,0,0.1)" }} />
    </span>
  );

  return (
    <Flex
      align="center"
      gap={10}
      style={{
        padding: "8px 0",
        borderBottom: isLast ? "none" : "1px solid #f5f5f5",
        cursor: "default",
      }}
    >
      <span style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", color: "rgba(0,0,0,0.45)" }}>
        <ContactsOutlined style={{ fontSize: ICON_SIZE }} />
      </span>
      <Text
        style={{ fontSize: 13, fontWeight: 500, flex: 1, minWidth: 0, color: "#15744b", cursor: "pointer" }}
        ellipsis
        onClick={() => go({ to: `/contacts/${contact.contactId}`, type: "push" })}
      >
        {contact.name}
      </Text>
      <Flex align="center" style={{ flexShrink: 0, width: 48, justifyContent: "space-between" }}>
        <span style={{ width: ICON_SIZE, display: "inline-flex", justifyContent: "center" }}>
          {quoteButton}
        </span>
        <span style={{ width: ICON_SIZE, display: "inline-flex", justifyContent: "center" }}>
          {whatsappIcon}
        </span>
      </Flex>
    </Flex>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

export const MisClientes = ({
  stores = [],
  selectedOperatorIds = [],
}: {
  stores?: Store[];
  /** El mismo valor que maneja el panel de filtros del panel de métricas. */
  selectedOperatorIds?: string[];
}) => {
  const { t } = useTranslation();
  const go = useGo();
  const { selectedStores } = useSelectedStores();
  // `null` = sin filtro de participación (alcance amplio del store). `[]` = se resolvió a nadie
  // (el/los operador(es) elegido(s) no tienen ficha de contacto ahí) — sin resultados, no "todos".
  const [participationFilter, setParticipationFilter] = useState<number[] | null>(null);
  const [filterReady, setFilterReady] = useState(false);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  // Scroll infinito: se piden de a PAGE_SIZE. `pagina` es la última ya cargada y `cargados` cuenta
  // lo que devolvió la API (antes de descartar las anclas), que es lo que se compara con `total`.
  const [pagina, setPagina] = useState(1);
  const [cargados, setCargados] = useState(0);
  const [cargandoMas, setCargandoMas] = useState(false);
  // Se descarta la respuesta de una carga vieja si mientras tanto cambió el filtro.
  const generacion = useRef(0);

  const sellerCodes = selectedStores.map((s) => s.code);

  // Los clientes siguen la unión de las selecciones activas (xcons-docs#310); el store completo es
  // todo destildado, igual que las métricas. La propia tarjeta ya viaja como un userId más en
  // `selectedOperatorIds` (xcons-docs#318), sin sentinel aparte.
  const hayFiltroDeOperador = selectedOperatorIds.length > 0;
  const targetOperatorIds = selectedOperatorIds.map(Number).filter(Number.isFinite);

  /**
   * El título lleva al listado de contactos con el MISMO recorte que muestra el widget (QA #273.8):
   * los que tienen relación conmigo, y el filtro de store de arriba.
   *
   * Antes navegaba a `/contacts?participant=me`, un query param que la grilla de refine no entiende
   * — llegaba sin ningún filtro.
   */
  const irAlListado = () => {
    const filters: any[] = [];
    if (participationFilter?.length) {
      filters.push({
        field: "participations.contactId",
        operator: participationFilter.length === 1 ? "eq" : "in",
        value: participationFilter.length === 1 ? participationFilter[0] : participationFilter,
      });
    }
    if (sellerCodes.length > 0) {
      filters.push({ field: "storeCode", operator: "in", value: sellerCodes });
    }
    go({ to: "/contacts", type: "push", query: { filters } });
  };

  // Resuelve a qué ficha(s) de contacto hay que anclar la búsqueda de "relacionados": una por cada
  // operador seleccionado (la propia incluida). Sin ningún operador elegido, no hace falta resolver
  // nada — es alcance amplio del store.
  useEffect(() => {
    setFilterReady(false);
    if (!hayFiltroDeOperador) {
      setParticipationFilter(null);
      setFilterReady(true);
      return;
    }
    if (targetOperatorIds.length === 0) return;

    const storeCodes = sellerCodes.filter(Boolean);
    const params: Record<string, string> = {
      "filter.userId": targetOperatorIds.length === 1 ? String(targetOperatorIds[0]) : `$in:${targetOperatorIds.join(",")}`,
    };
    if (storeCodes.length > 0) params["filter.storeCode"] = storeCodes.join(",");
    axiosInstance
      .get(API_URL_CONTACTS, { params })
      .then(({ data }) => {
        const ids = (data.data ?? []).map((c: any) => Number(c.contactId)).filter(Boolean);
        setParticipationFilter(ids);
      })
      .catch(() => setParticipationFilter([]))
      .finally(() => setFilterReady(true));
  }, [hayFiltroDeOperador, targetOperatorIds.join(","), sellerCodes.join(",")]);

  /** Pide una página de clientes con los filtros actuales. Las anclas (el propio operador) se descartan. */
  const pedirPagina = async (page: number) => {
    const params: Record<string, string> = {
      limit: String(PAGE_SIZE),
      page: String(page),
      sortBy: "createdAt:DESC",
    };
    if (participationFilter && participationFilter.length > 0) {
      params["filter.participations.contactId"] = participationFilter.length === 1
        ? `$eq:${participationFilter[0]}`
        : `$in:${participationFilter.join(",")}`;
    }
    if (sellerCodes.length > 0) params["filter.storeCode"] = `$in:${sellerCodes.join(",")}`;

    const { data } = await axiosInstance.get(API_URL_CONTACTS, { params });
    const pagination = data.meta?.pagination ?? data.meta;
    const anclas = new Set(participationFilter ?? []);
    const crudos: any[] = data.data ?? [];
    const rows: ContactRow[] = crudos
      .filter((c: any) => !anclas.has(Number(c.contactId)))
      .map((c: any) => ({
        contactId: c.contactId,
        userId: Number(c.userId ?? c.user?.userId ?? c.user?.entityId) || null,
        name: `${c.user?.firstname ?? ""} ${c.user?.lastname ?? ""}`.trim() || c.user?.email || "—",
        email: c.user?.email ?? "",
        whatsapp: c.user?.whatsapp ?? "",
        storeCode: c.storeCode ?? "",
        createdAt: c.createdAt ?? "",
      }));
    return { total: pagination?.totalItems ?? 0, crudos: crudos.length, rows };
  };

  useEffect(() => {
    if (!filterReady) return;
    // Se pidió un operador puntual y no tiene ficha de contacto en este alcance: son cero
    // resultados, no "todos" — no hay que caer al alcance amplio por default.
    if (hayFiltroDeOperador && (participationFilter?.length ?? 0) === 0) {
      setContacts([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    const mia = ++generacion.current;
    pedirPagina(1)
      .then(({ total: t, crudos, rows }) => {
        if (mia !== generacion.current) return;
        setTotal(t);
        setCargados(crudos);
        setPagina(1);
        setContacts(rows);
      })
      .catch(() => mia === generacion.current && setContacts([]))
      .finally(() => mia === generacion.current && setLoading(false));
  }, [filterReady, hayFiltroDeOperador, participationFilter?.join(","), sellerCodes.join(",")]);

  const filtered = search.trim()
    ? contacts.filter((c) => {
        const q = search.toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          c.whatsapp.includes(q)
        );
      })
    : contacts;

  const hayMas = cargados < total;

  const cargarMas = () => {
    if (loading || cargandoMas || !hayMas) return;
    const mia = generacion.current;
    setCargandoMas(true);
    pedirPagina(pagina + 1)
      .then(({ crudos, rows }) => {
        if (mia !== generacion.current) return;
        setPagina((p) => p + 1);
        setCargados((c) => c + crudos);
        setContacts((prev) => [...prev, ...rows]);
      })
      .catch(() => {})
      .finally(() => setCargandoMas(false));
  };

  const alScrollear = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) cargarMas();
  };

  /**
   * Alta de contacto desde el panel lateral, el mismo que usa el listado (QA #273.9).
   *
   * Antes navegaba a `/contacts/create`, que no es una ruta de creación: abría una ficha vacía. Y
   * al guardar, acá sí se va a la ficha del contacto nuevo — es lo que uno espera cuando lo crea
   * desde un widget, a diferencia del listado, donde alcanza con refrescar la grilla.
   */
  const handleCreated = (creado: any) => {
    const id = creado?.contactId ?? creado?.contact_id;
    if (id) go({ to: `/contacts/${id}`, type: "push" });
  };

  /**
   * Crea un presupuesto a nombre de este contacto (QA #273.14).
   *
   * **`customerId` es el `user_id`, no el `contact_id`.** Son espacios de ids distintos: el backend
   * escribe `puntoseco_wishlist.customer_id` con ese valor y `assignCustomerAsOwner` busca
   * `contact WHERE user_id = ?`. Pasándole el `contact_id` esa búsqueda no encuentra nada, sale sin
   * hacer nada, y el presupuesto nace sin dueño — que es justo lo que reportó QA.
   */
  const handleCreateQuoteForContact = async (userId: number | null, storeCode: string) => {
    const store = stores.find((s) => String(s.code ?? s.store_id ?? s.storeId) === storeCode);
    const storeId = Number(store?.store_id ?? store?.storeId);
    if (!Number.isFinite(storeId)) {
      message.error("No se encontró el store");
      return;
    }
    if (!userId) {
      message.error("El contacto no tiene identidad asociada");
      return;
    }
    try {
      const { data } = await axiosInstance.post(API_URL_QUOTES, { storeId, customerId: userId });
      const newId = data?.quoteId;
      if (!newId) { message.error("No se recibió el id del presupuesto"); return; }
      window.open(`/quotes/${newId}`, "_blank");
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message || err?.response?.data?.error;
      message.error(Array.isArray(apiMsg) ? apiMsg.join(", ") : apiMsg || "Error al crear presupuesto");
    }
  };

  const hasSingleStore = selectedStores.length === 1;

  // El store no se elige antes de abrir: el propio panel lo pide, y así el flujo es uno solo
  // tenga el operador uno o veinte stores.
  const plusButton = (
    <LateralModal
      title="Nuevo contacto"
      buttonType="primary"
      buttonShape="default"
      buttonSize="small"
      // `icon` es el booleano que habilita a `iconSvg`: sin él el botón sale vacío, sin ícono ni
      // texto. Está así en `LateralModal` (`icon={icon ? iconSvg : null}`).
      icon
      iconSvg={<PlusOutlined />}
      noPadding
    >
      <ContactsCreate
        // `t` NO es opcional en la práctica aunque la firma lo diga: el render llama `t("name")`,
        // así que sin esto el panel explota al abrirse y deja la pantalla en blanco.
        t={t}
        formProps={{}}
        storesSelectProps={stores}
        onCreated={handleCreated}
      />
    </LateralModal>
  );

  return (
    <Card
      styles={{
        header: {
          background: "rgba(0,0,0,0.02)",
          borderBottom: "1px solid #f0f0f0",
          minHeight: 40,
          padding: "0 16px",
        },
        body: { padding: 0, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
      }}
      style={{ overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}
      title={
        <Flex align="center" gap={8} style={{ cursor: "pointer" }}
          onClick={irAlListado}
        >
          <RelationIcon style={{ fontSize: 16, color: "#15744b" }} />
          <Text strong style={{ fontSize: 14, color: "#15744b" }}>Clientes relacionados</Text>
        </Flex>
      }
      extra={
        <Flex align="center" gap={8}>
          <Text style={{ fontSize: 14, color: "rgba(0,0,0,0.45)", opacity: loading ? 0.4 : 1 }}>
            {total || ""}
          </Text>
          {plusButton}
        </Flex>
      }
    >
      <div style={{ padding: "8px 16px", flexShrink: 0 }}>
        <Input.Search
          placeholder="Ingresá nombre, teléfono o email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "0 16px" }} onScroll={alScrollear}>
        {filtered.length === 0 && !loading && (
          <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.35)" }}>
            {search ? "Sin resultados" : "Sin clientes relacionados"}
          </Text>
        )}
        {filtered.map((c, i) => (
          <ClientRow
            key={c.contactId}
            contact={c}
            isLast={i === filtered.length - 1}
            stores={stores}
            selectedStores={selectedStores}
            onCreateQuote={handleCreateQuoteForContact}
          />
        ))}
        {(cargandoMas || (hayMas && !search)) && (
          <Text style={{ display: "block", textAlign: "center", padding: "8px 0", fontSize: 12, color: "rgba(0,0,0,0.35)" }}>
            Cargando más…
          </Text>
        )}
      </div>
    </Card>
  );
};
